import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Table, Form, Input, Select, Button, Space, Tag, Typography, Tooltip, Modal, Tabs,
  DatePicker, Radio, Upload, Drawer, Divider, Alert, InputNumber, Checkbox, Row, Col, message, Statistic, Empty
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, PlusOutlined, ArrowLeftOutlined, DownloadOutlined,
  InfoCircleOutlined, CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, UploadOutlined,
  ExperimentOutlined, CopyOutlined, EditOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const MOCK_FW = {
  P2MYHLjna9: [
    { key: '1', fwName: '主控固件', fwVersion: 'V3.0.0', sdk: '12', fwType: ['生产固件', '升级固件'], hasConfig: true, test: 'wait', ota: 'wait', testItems: ['图像测试', 'WIFI信号', '电池测试'], otaTest: true, otaTargetVersion: 'V3.1.0', otaFileName: 'main_fw_v3.1.0_ota.bin', desc: '', uploadTime: '2026-04-10 14:30:00', modelSource: 'inherit' },
    { key: '6', fwName: '电源管理固件', fwVersion: 'V1.0.0', sdk: '6', fwType: ['生产固件'], hasConfig: false, test: 'wait', ota: 'wait', testItems: ['电源插口测试', '电池测试'], otaTest: false, desc: '电源模块专用固件', uploadTime: '2026-04-10 15:00:00', modelSource: 'inherit' },
    { key: '2', fwName: 'WiFi模组固件', fwVersion: 'V2.1.0', sdk: '8', fwType: ['生产固件'], hasConfig: false, test: 'testing', ota: 'wait', testItems: ['WIFI信号', '蓝牙测试'], otaTest: false, desc: '', uploadTime: '2026-04-08 09:00:00', modelSource: 'inherit' },
    { key: '3', fwName: 'MCU固件', fwVersion: 'V1.5.0', sdk: '5', fwType: ['升级固件'], hasConfig: true, test: 'pass', ota: 'wait', desc: '用于测试 OTA 联动', uploadTime: '2026-04-05 16:20:00', modelSource: 'inherit' },
    { key: '4', fwName: '蓝牙模组固件', fwVersion: 'V1.3.0', sdk: '5', fwType: ['生产固件'], hasConfig: false, test: 'pass', ota: 'testing', testItems: ['蓝牙测试', '复位测试', '电源插口测试'], desc: '修复已知问题', uploadTime: '2026-04-03 10:00:00', modelSource: 'inherit' },
    { key: '5', fwName: '传感器固件', fwVersion: 'V2.0.1', sdk: '8', fwType: ['生产固件'], hasConfig: true, test: 'pass', ota: 'pass', testItems: ['图像测试', 'PIR检测', 'PTZ测试', '麦克风测试'], desc: '全功能测试完成', uploadTime: '2026-04-01 09:00:00', modelSource: 'custom' },
  ],
  Pm38jEddh2: [
    { key: '1', fwName: '主控固件', fwVersion: 'V1.0.0', sdk: '3', fwType: ['生产固件'], hasConfig: false, test: 'pass', ota: 'testing', testItems: ['图像测试', 'WIFI信号'], desc: '初始版本', uploadTime: '2026-04-08 09:00:00', modelSource: 'inherit' },
  ],
  PHWEu9JFZd: [
    { key: '1', fwName: '网关固件', fwVersion: 'V2.0.0', sdk: '10', fwType: ['生产固件', '升级固件'], hasConfig: true, test: 'pass', ota: 'pass', testItems: ['WIFI信号', '指示灯测试', '复位测试'], desc: '全功能版本', uploadTime: '2026-04-05 16:20:00', modelSource: 'custom' },
    { key: '2', fwName: '网关固件', fwVersion: 'V2.1.0', sdk: '10', fwType: ['升级固件'], hasConfig: false, test: 'wait', ota: 'wait', desc: 'OTA升级包', uploadTime: '2026-04-03 10:00:00', modelSource: 'inherit' },
  ],
};

const MOCK_GENERATED_LICENSES = [
  { key: '1', license: 'A01T012605061111', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:00:00' },
  { key: '2', license: 'A01T022605062222', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:05:00' },
  { key: '3', license: 'A01T032605063333', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:10:00' },
  { key: '4', license: 'A01T042605064444', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:15:00' },
  { key: '5', license: 'A01T052605065555', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:20:00' },
  { key: '6', license: 'A01T062605066666', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:25:00' },
  { key: '7', license: 'A01T072605067777', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:30:00' },
  { key: '8', license: 'A01T082605068888', type: 'test', fwName: '主控固件', fwVersion: 'V3.0.0', generateTime: '2026-04-10 10:35:00' },
];

const TEST_STATUS = {
  upgrading: { text: '升级中', color: 'processing' },
  success: { text: '成功', color: 'success' },
  fail: { text: '失败', color: 'error' },
};

const TEST_ITEMS = [
  '图像测试', 'WIFI信号', '电池测试', 'TF卡读写', '指示灯测试',
  '麦克风测试', '喇叭测试', 'PTZ测试', 'PIR检测', '蓝牙测试',
  '复位测试', '呼叫测试', '电源插口测试',
];

const FirmwareDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId') || '';
  const productName = decodeURIComponent(searchParams.get('productName') || '');
  const enterpriseName = decodeURIComponent(searchParams.get('enterpriseName') || '');
  const enterpriseId = searchParams.get('enterpriseId') || '';
  const fwTypeParam = searchParams.get('fwType') || '';
  // mock：有企业信息=平台角色，无=企业角色
  const [mockRole, setMockRole] = React.useState(enterpriseName ? 'platform' : 'enterprise');
  const isPlatform = mockRole === 'platform';
  const source = searchParams.get('source') || 'self';
  const isEcosystem = source === 'ecosystem';
  const [form] = Form.useForm();
  const [finalizedKeys, setFinalizedKeys] = useState(['fw_3', '4', '5']); // mock：已定版
  const [createForm] = Form.useForm();
  const [licenseForm] = Form.useForm();
  const [otaForm] = Form.useForm();
  const rawFw = MOCK_FW[productId] || MOCK_FW['P2MYHLjna9'];
  const [dataSource, setDataSource] = useState(rawFw);
  const [createVisible, setCreateVisible] = useState(false);
  const [fwConfigUploaded, setFwConfigUploaded] = useState(false);
  const [fwConfigVisible, setFwConfigVisible] = useState(false);
  const [fwConfigEnabled, setFwConfigEnabled] = useState(false);
  const [viewConfigVisible, setViewConfigVisible] = useState(false);
  // 测试弹窗
  const [licenseModalVisible, setLicenseModalVisible] = useState(false);
  const [otaModalVisible, setOtaModalVisible] = useState(false);
  const [pendingRecord, setPendingRecord] = useState(null);
  const [generatedLicenses, setGeneratedLicenses] = useState([...MOCK_GENERATED_LICENSES].reverse());
  // 按固件key管理测试授权余额
  const [quotaByFw, setQuotaByFw] = useState({
    '1': { used: 8, total: 13 },
  });
  // eslint-disable-next-line no-unused-vars
  const getCurrentQuota = () => {
    if (!pendingRecord) return { used: 0, total: 10 };
    return quotaByFw[pendingRecord.key] || { used: 0, total: 10 };
  };
  // 查看已生成License弹窗
  const [viewLicensesVisible, setViewLicensesVisible] = useState(false);
  // 测试记录
  const [testRecordVisible, setTestRecordVisible] = useState(false);
  const [testRecordTitle, setTestRecordTitle] = useState('');
  const [testFwVersion, setTestFwVersion] = useState('');
  const [licenseSearch, setLicenseSearch] = useState('');
  // 添加测试设备
  const [addTestVisible, setAddTestVisible] = useState(false);
  const [addTestType, setAddTestType] = useState('normal');
  const [testDeviceForm] = Form.useForm();
  // 修改生产测试项
  const [editTestItemsVisible, setEditTestItemsVisible] = useState(false);
  const [editTestItemsRecord, setEditTestItemsRecord] = useState(null);
  const [editTestItemsForm] = Form.useForm();
  // 编辑固件类型
  const [editTypeVisible, setEditTypeVisible] = useState(false);
  const [editTypeRecord, setEditTypeRecord] = useState(null);
  const [editTypeForm] = Form.useForm();
  const [editFwConfigEnabled, setEditFwConfigEnabled] = useState(false);
  const [editFwConfigUploaded, setEditFwConfigUploaded] = useState(false);
  // 固件功能测试记录
  const [fwTestRecords, setFwTestRecords] = useState([
    { key: '1', time: '2026-04-10 10:00:00', license: 'A01T012605061111', status: 'success', deviceVersion: 'V3.0.0' },
    { key: '2', time: '2026-04-10 09:30:00', license: 'A01T022605062222', status: 'upgrading' },
    { key: '3', time: '2026-04-09 14:00:00', license: 'A01T032605063333', status: 'fail' },
    { key: '4', time: '2026-04-11 08:30:00', license: 'A01T042605064444', status: 'success', deviceVersion: 'V2.8.0' },
  ]);
  // OTA能力测试记录
  const [otaTestRecords, setOtaTestRecords] = useState([
    { key: '1', time: '2026-04-10 10:20:00', license: 'A01T052605065555', status: 'success', targetVersion: 'V3.0.0', deviceVersion: 'V3.0.0' },
    { key: '2', time: '2026-04-10 09:45:00', license: 'A01T062605066666', status: 'upgrading', targetVersion: 'V2.1.0' },
    { key: '3', time: '2026-04-11 09:00:00', license: 'A01T072605067777', status: 'success', targetVersion: 'V3.0.0', deviceVersion: 'V2.5.0' },
  ]);

  const handleConfirmTest = (record, type) => {
    if (type === 'test' && record.test === 'wait') {
      setPendingRecord(record);
      licenseForm.resetFields();
      setLicenseModalVisible(true);
      return;
    }
    if (type === 'ota' && record.ota === 'wait') {
      setPendingRecord(record);
      otaForm.resetFields();
      setOtaModalVisible(true);
      return;
    }
    Modal.confirm({
      title: '通过确认',
      icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
      content: type === 'test'
        ? '你确定此固件的功能通过测试，且功能在App上表现正常吗？'
        : '你正在进行此固件版本的 OTA 能力测试通过～',
      okText: '继续', cancelText: '取消', centered: true,
      onOk: () => {
        setDataSource(prev => prev.map(d => d.key === record.key ? { ...d, [type === 'test' ? 'test' : 'ota']: 'pass' } : d));
        message.success('已确认通过');
      },
    });
  };

  const handleLicenseSubmit = () => {
    licenseForm.validateFields().then(() => {
      setDataSource(prev => prev.map(d => d.key === pendingRecord.key ? { ...d, test: 'testing' } : d));
      setLicenseModalVisible(false);
      message.success('已发起固件测试');
    }).catch(() => {});
  };

  const handleOtaSubmit = () => {
    otaForm.validateFields().then(() => {
      setDataSource(prev => prev.map(d => d.key === pendingRecord.key ? { ...d, ota: 'testing' } : d));
      setOtaModalVisible(false);
      message.success('已发起OTA能力测试');
    }).catch(() => {});
  };

  const renderTestCol = (text, record, type) => {
    if (text === 'wait') return (
      <Space direction="vertical" size={0}>
        <Space size={4}><CloseCircleFilled style={{ color: '#bfbfbf', fontSize: 14 }} /><Text style={{ fontSize: 12 }}>待测试</Text></Space>
        {(type === 'test' || record.test === 'pass') && <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }} onClick={() => handleConfirmTest(record, type)}>{type === 'test' ? '测试固件' : '测试OTA功能'}</Button>}
      </Space>
    );
    if (text === 'testing') return (
      <Space direction="vertical" size={0}>
        <Space size={4}><ExclamationCircleFilled style={{ color: '#ff4d4f', fontSize: 14 }} /><Text style={{ fontSize: 12 }}>测试中</Text></Space>
        {(type === 'test' || record.test === 'pass') && <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }} onClick={() => handleConfirmTest(record, type)}>确认通过</Button>}
      </Space>
    );
    return (
      <Space direction="vertical" size={0}>
        <Space size={4}><CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} /><Text style={{ fontSize: 12 }}>{type === 'test' ? '测试通过' : '测试成功'}</Text></Space>
        {type === 'test' && (
          finalizedKeys.includes(record.key)
            ? <Tag color="green" style={{ border: 'none', fontSize: 11 }}>已定版</Tag>
            : <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }} onClick={() => {
                Modal.confirm({
                  title: '确认固件定版',
                  content: <>1、产品物模型将锁定为只读<br />2、可应用生产工单——"生产设备"和"发布设备OTA"全量升级。<br />后续固件需独立配置物模型。<br />确定定版吗？</>,
                  okText: '确认定版',
                  cancelText: '取消',
                  onOk: () => { setFinalizedKeys(prev => [...prev, record.key]); message.success('已定版，产品物模型已锁定'); },
                });
              }}>确认定版</Button>
        )}
      </Space>
    );
  };

  const testRecordColumns = [
    { title: '测试时间', dataIndex: 'time', width: 160 },
    { title: 'License', dataIndex: 'license', width: 180, render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text> },
    { title: '状态', dataIndex: 'status', width: 90, render: (v) => { const s = TEST_STATUS[v]; return <Tag color={s.color}>{s.text}</Tag>; } },
    {
      title: '设备上报固件版本', key: 'deviceVersion', width: 160,
      render: (_, r) => {
        if (r.status !== 'success' || !r.deviceVersion) return <Text type="secondary">—</Text>;
        const match = r.deviceVersion === testFwVersion;
        return match
          ? <Text code>{r.deviceVersion}</Text>
          : <Tooltip title="设备上报版本与上传固件版本不一致，可能存在风险"><Text style={{ color: '#ff4d4f', fontFamily: 'monospace' }}>{r.deviceVersion} <InfoCircleOutlined style={{ fontSize: 12 }} /></Text></Tooltip>;
      }
    },
    {
      title: '操作', key: 'action', width: 100,
      render: (_, r) => r.status === 'upgrading'
        ? <Tooltip title="升级中不可再次下发"><Button type="link" size="small" disabled>再次下发</Button></Tooltip>
        : <Button type="link" size="small" onClick={() => {
            const newRecord = { key: String(Date.now()), time: new Date().toLocaleString('zh-CN'), license: r.license, status: 'upgrading' };
            setFwTestRecords(prev => [newRecord, ...prev]);
            message.success(`已对 License ${r.license} 再次下发测试`);
          }}>再次下发</Button>,
    },
  ];

  const otaRecordColumns = [
    { title: '测试时间', dataIndex: 'time', width: 160 },
    { title: 'License', dataIndex: 'license', width: 180, render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text> },
    { title: '目标版本', dataIndex: 'targetVersion', width: 120 },
    { title: '状态', dataIndex: 'status', width: 90, render: (v) => { const s = TEST_STATUS[v]; return <Tag color={s.color}>{s.text}</Tag>; } },
    {
      title: '设备上报固件版本', key: 'deviceVersion', width: 160,
      render: (_, r) => {
        if (r.status !== 'success' || !r.deviceVersion) return <Text type="secondary">—</Text>;
        const match = r.deviceVersion === r.targetVersion;
        return match
          ? <Text code>{r.deviceVersion}</Text>
          : <Tooltip title="设备上报版本与 OTA 目标版本不一致，可能存在升级风险"><Text style={{ color: '#ff4d4f', fontFamily: 'monospace' }}>{r.deviceVersion} <InfoCircleOutlined style={{ fontSize: 12 }} /></Text></Tooltip>;
      }
    },
    {
      title: '操作', key: 'action', width: 100,
      render: (_, r) => r.status === 'upgrading'
        ? <Tooltip title="升级中不可再次下发"><Button type="link" size="small" disabled>再次下发</Button></Tooltip>
        : <Button type="link" size="small" onClick={() => {
            const newRecord = { key: String(Date.now()), time: new Date().toLocaleString('zh-CN'), license: r.license, status: 'upgrading', targetVersion: r.targetVersion };
            setOtaTestRecords(prev => [newRecord, ...prev]);
            message.success(`已对 License ${r.license} 再次下发 OTA 测试`);
          }}>再次下发</Button>,
    },
  ];

  const columns = [
    {
      title: '固件信息', dataIndex: 'fwName', width: 180,
      render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.fwName}</div><Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.fwVersion}</Text><Button type="link" size="small" icon={<DownloadOutlined />} style={{ padding: 0 }} onClick={() => message.success('下载成功')} /></div>),
    },
    {
      title: '物模型来源', dataIndex: 'modelSource', width: 100,
      render: (v) => v === 'custom'
        ? <Tag color="#ff4d4f" style={{ fontWeight: 600, border: 'none', fontSize: 12 }}><Tooltip title="该固件有独立物模型配置，不再跟随产品变更">固件定义</Tooltip></Tag>
        : <Tag color="#8c8c8c" style={{ border: 'none', fontSize: 12 }}><Tooltip title="该固件物模型跟随产品配置，产品变更后自动生效">继承产品</Tooltip></Tag>,
    },
    { title: 'SDK版本', dataIndex: 'sdk', width: 110 },
    { title: '固件类型', dataIndex: 'fwType', width: 170, render: (v, r) => {
      const types = Array.isArray(v) ? v : [v];
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {types.map(t => <Tag key={t} color={t === '生产固件' ? 'blue' : 'orange'} style={{ margin: 0 }}>{t}</Tag>)}
          </div>
          <Tooltip title="编辑类型">
            <Button type="link" size="small" icon={<EditOutlined />} style={{ padding: '0 4px', fontSize: 12, lineHeight: 1, height: 20 }}
              onClick={() => { setEditTypeRecord(r); editTypeForm.setFieldsValue({ fwType: Array.isArray(r.fwType) ? [...r.fwType] : [r.fwType], testItems: r.testItems || [] }); setEditFwConfigEnabled(r.hasConfig || false); setEditFwConfigUploaded(false); setEditTypeVisible(true); }} />
          </Tooltip>
        </div>
      );
    } },
    {
      title: '生产测试项', dataIndex: 'testItems', width: 160,
      render: (v, r) => {
        const types = Array.isArray(r.fwType) ? r.fwType : [r.fwType];
        return types.includes('生产固件')
        ? <Button type="link" size="small" style={{ padding: 0 }} onClick={() => { setEditTestItemsRecord(r); editTestItemsForm.setFieldsValue({ testItems: r.testItems || [], otaTest: r.otaTest || false, otaTargetVersion: r.otaTargetVersion || '' }); setEditTestItemsVisible(true); }}>{(v || []).length ? `${v.length}项` : '点击配置'}</Button>
        : <Text type="secondary">—</Text>;
      },
    },
    {
      title: '固件配置', dataIndex: 'hasConfig', width: 140,
      render: (v, r) => v
        ? <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setViewConfigVisible(true)}>查看</Button>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => message.success(`${r.fwName} ${r.fwVersion} 配置已下载`)}>下载</Button>
          </Space>
        : <Text type="secondary">无</Text>,
    },
    {
      title: <Space size={4}>固件测试<Tooltip title="此项必测，只有测试后人工确认通过后且确认定版，才可进行量产/全量设备OTA！"><InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 13 }} /></Tooltip></Space>,
      dataIndex: 'test', width: 120, render: (text, record, index) => renderTestCol(text, record, 'test', index),
    },
    {
      title: <Space size={4}>OTA能力测试<Tooltip title="用于检测固件的 OTA 功能是否丢失！"><InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 13 }} /></Tooltip></Space>,
      dataIndex: 'ota', width: 130, render: (text, record, index) => renderTestCol(text, record, 'ota', index),
    },
    { title: '固件说明', dataIndex: 'desc', width: 160, render: (v) => v || <Text type="secondary">—</Text> },
    { title: '更新时间', dataIndex: 'uploadTime', width: 170 },
    {
      title: '操作', key: 'action', width: 230, fixed: 'right',
      render: (_, r) => isEcosystem ? (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" disabled={r.test === 'wait'} onClick={() => { setTestRecordTitle(`${r.fwName} ${r.fwVersion}`); setTestFwVersion(r.fwVersion || ''); setLicenseSearch(''); setTestRecordVisible(true); }}>          测试记录</Button>
          <Button type="link" size="small" onClick={() => navigate(`/firmware/model-config?productId=${productId}&productName=${encodeURIComponent(productName)}&fwId=${r.fwId || ''}&fwName=${encodeURIComponent(r.fwName)}&fwVersion=${encodeURIComponent(r.fwVersion)}&isFinalized=${finalizedKeys.includes(r.key) ? 1 : 0}&modelSource=${r.modelSource || 'inherit'}&role=${mockRole}&source=ecosystem`)}>{isEcosystem && (r.modelSource || 'inherit') !== 'custom' ? '查看物模型' : isPlatform ? '管理物模型' : finalizedKeys.includes(r.key) ? '查看物模型' : '编辑物模型'}</Button>
        </Space>
      ) : (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" disabled={r.test === 'wait'} onClick={() => { setTestRecordTitle(`${r.fwName} ${r.fwVersion}`); setTestFwVersion(r.fwVersion || ''); setLicenseSearch(''); setTestRecordVisible(true); }}>测试记录</Button>
          <Button type="link" size="small" onClick={() => navigate(`/firmware/model-config?productId=${productId}&productName=${encodeURIComponent(productName)}&fwId=${r.fwId || ''}&fwName=${encodeURIComponent(r.fwName)}&fwVersion=${encodeURIComponent(r.fwVersion)}&isFinalized=${finalizedKeys.includes(r.key) ? 1 : 0}&modelSource=${r.modelSource || 'inherit'}&role=${mockRole}`)}>{isPlatform ? '管理物模型' : finalizedKeys.includes(r.key) ? '查看物模型' : '编辑物模型'}</Button>
          <Button type="link" size="small" danger onClick={() => {
            const doDelete = () => { setDataSource(prev => prev.filter(d => d.key !== r.key)); message.success('已删除'); };
            if (r.test === 'pass') {
              Modal.confirm({
                title: '风险提示',
                content: '当前固件测试已通过，继续删除可能会影响此固件的生产和设备OTA，请谨慎操作！',
                okText: '确认',
                cancelText: '取消',
                okButtonProps: { danger: true },
                onOk: doDelete,
              });
            } else {
              Modal.confirm({
                title: '确认删除',
                content: `确定删除固件「${r.fwName} ${r.fwVersion}」？`,
                okText: '删除',
                okType: 'danger',
                cancelText: '取消',
                onOk: doDelete,
              });
            }
          }}>删除</Button>
        </Space>
      ),
    },
  ];

  const isReadOnly = false;
  const displayColumns = columns.filter(() => true);

  const filteredFw = fwTestRecords.filter(i => i.license.toLowerCase().includes(licenseSearch.toLowerCase()));
  const filteredOta = otaTestRecords.filter(i => i.license.toLowerCase().includes(licenseSearch.toLowerCase()));

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/firmware')} />
          <Title level={4} style={{ margin: 0 }}>固件版本</Title>
        </div>
        <Space>
          <Text type="secondary">Mock角色：</Text>
          <Radio.Group value={mockRole} onChange={e => setMockRole(e.target.value)} optionType="button" buttonStyle="solid" size="small">
            <Radio.Button value="enterprise">企业角色</Radio.Button>
            <Radio.Button value="platform">平台角色</Radio.Button>
          </Radio.Group>
        </Space>
      </div>

      <Card bordered={false} size="small" style={{ marginBottom: 16, background: '#fafafa', borderRadius: 8 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space size={24}>
          <span><Text type="secondary">产品名称：</Text><Text strong>{productName}</Text></span>
          <span><Text type="secondary">产品ID：</Text><Text copyable style={{ fontFamily: 'monospace' }}>{productId}</Text>
            {isEcosystem && <Tag color="blue" style={{ marginLeft: 8 }}>派生产品</Tag>}
          </span>
          {enterpriseName && <span><Text type="secondary">所属企业：</Text><Text strong>{enterpriseName}</Text><Text type="secondary" style={{ marginLeft: 4 }}>企业ID：{enterpriseId}</Text></span>}
          {isEcosystem && <span><Text type="secondary">固件由方案商提供和维护</Text></span>}
        </Space>
      </Card>
      {isEcosystem && (
        <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }} message="当前产品为派生产品，固件由方案商提供。方案商全部固件默认可见，可用于生产工单和OTA升级。" />
      )}

      <Form form={form} layout="inline" style={{ marginBottom: 16, gap: '8px 0' }} initialValues={{ fwType: fwTypeParam ? [fwTypeParam] : undefined }}>
        <Form.Item name="fwName" style={{ marginRight: 8 }}><Input placeholder="固件名称" allowClear style={{ width: 160 }} /></Form.Item>
        <Form.Item name="fwVersion" style={{ marginRight: 8 }}><Input placeholder="固件版本" allowClear style={{ width: 160 }} /></Form.Item>
        <Form.Item name="fwType" style={{ marginRight: 8 }}>
          <Select placeholder="固件类型" allowClear mode="multiple" style={{ width: 180 }}>
            <Select.Option value="生产固件">生产固件</Select.Option>
            <Select.Option value="升级固件">升级固件</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="uploadTime" style={{ marginRight: 8 }}><RangePicker placeholder={['开始时间', '结束时间']} /></Form.Item>
        <Form.Item><Space><Button type="primary" icon={<SearchOutlined />}>查询</Button><Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button></Space></Form.Item>
      </Form>

      <Card bordered={false}>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          {!isEcosystem && <Button type="primary" icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setCreateVisible(true); }}>新增固件</Button>}
          {!isReadOnly && <Button
            style={{ background: '#faad14', color: '#fff', borderColor: '#faad14' }}
            onClick={() => setViewLicensesVisible(true)}
          >
            测试License
          </Button>}
        </div>
        <Table columns={displayColumns} dataSource={dataSource} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 新增固件 */}
      <Drawer title="新增固件" width={600} open={createVisible} onClose={() => { setCreateVisible(false); setFwConfigUploaded(false); setFwConfigEnabled(false); }} destroyOnClose
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button onClick={() => { setCreateVisible(false); setFwConfigUploaded(false); setFwConfigEnabled(false); }}>取消</Button><Button onClick={() => { createForm.validateFields().then((values) => { message.success('固件已上传'); setCreateVisible(false); setFwConfigUploaded(false); setFwConfigEnabled(false); navigate(`/firmware/model-config?productId=${productId}&productName=${encodeURIComponent(productName)}&fwName=${encodeURIComponent(values.fwName || '')}&fwVersion=${encodeURIComponent(values.fwVersion || '')}&fwType=${encodeURIComponent(Array.isArray(values.fwType) ? values.fwType.join(',') : (values.fwType || ''))}&desc=${encodeURIComponent(values.desc || '')}`); }).catch(() => {}); }}>保存并配置物模型</Button><Button type="primary" onClick={() => { createForm.validateFields().then(() => { message.success('固件已上传'); setCreateVisible(false); setFwConfigUploaded(false); setFwConfigEnabled(false); }).catch(() => {}); }}>保存</Button></div>}>
        <Form form={createForm} layout="vertical" initialValues={{ fwType: ['生产固件'] }}>
          <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6 }}>
            <Text type="secondary">产品：</Text><Text strong>{productName}</Text> <Text type="secondary" style={{ marginLeft: 8 }}>{productId}</Text>
          </div>
          <Form.Item name="fwName" label="固件名称" rules={[{ required: true, message: '请输入固件名称' }]}><Input placeholder="请输入固件名称" /></Form.Item>
          <Form.Item name="fwVersion" label="固件版本" rules={[{ required: true, message: '请输入固件版本' }]}><Input placeholder="请输入固件版本号" /></Form.Item>
          <Form.Item name="fwType" label="固件类型" rules={[{ required: true, message: '请至少选择一种固件类型', type: 'array', min: 1 }]}>
            <Checkbox.Group options={[{ label: '生产固件', value: '生产固件' }, { label: '升级固件', value: '升级固件' }]} />
          </Form.Item>
          <Form.Item name="file" label="上传固件" rules={[{ required: true, message: '请上传固件' }]}>
            <Upload maxCount={1} beforeUpload={() => false}><Button icon={<UploadOutlined />}>选择文件</Button></Upload>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => JSON.stringify(prev.fwType) !== JSON.stringify(cur.fwType)}>
            {({ getFieldValue }) => (getFieldValue('fwType') || []).includes('生产固件') ? (
              <>
                <Form.Item label={<span>固件配置 <Tooltip title="当前固件是否需要TF卡固件配置？"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>}>
                  <Radio.Group value={fwConfigEnabled} onChange={(e) => { setFwConfigEnabled(e.target.value); if (!e.target.value) { setFwConfigUploaded(false); } }}>
                    <Radio value={false}>否</Radio>
                    <Radio value={true}>是</Radio>
                  </Radio.Group>
                </Form.Item>
          {fwConfigEnabled && (
            <Form.Item label="上传固件配置">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Upload maxCount={1} beforeUpload={() => { setFwConfigUploaded(true); return false; }} showUploadList={false} accept=".json">
                  <Button icon={<UploadOutlined />}>上传固件配置</Button>
                </Upload>
                {fwConfigUploaded
                  ? <Button type="link" onClick={() => setFwConfigVisible(true)}>修改配置</Button>
                  : <Button type="link" icon={<DownloadOutlined />} onClick={() => {
                      const demo = `{
  "itemList": [ //配置项列表
    {
      "key": "HWType", //配置项键值，自定义
      "keyName": "硬件类型", //配置项键值名称，自定义
      "valueType": "0", //值类型 0：整形可选值。界面为选择框 1：整形范围值值，界面展示为输入框 2：字符串类型。界面为字符串输入框
      "valueArray": [ //值选项列表 valueType为0时有效
        {
          "value": "0", //值数据，自定义
          "valueName": "未定义" //值名称，自定义
        },
        {
          "value": "1",
          "valueName": "摇头机"
        }
      ]
    },
    {
      "key": "TdAdjustStepY", //配置项键值
      "keyName": "校准步长Y", //配置项键值名称
      "valueType": "1", //值类型 0：整形可选值。界面为选择框 1：整形范围值值，界面展示为输入框 2：字符串类型。界面为字符串输入框
      "minValue": "1", //值范围最小值 valueType为1时有效
      "maxValue": "10000" //值范围最大值 valueType为1时有效
    },
    {
      "key": "osd", //配置项键值
      "keyName": "OSD", //配置项键值名称
      "valueType": "2", //值类型 0：整形可选值。界面为选择框 1：整形范围值值，界面展示为输入框 2：字符串类型。界面为字符串输入框
      "maxLen": "1024" //字符串最大长度
    }
  ]
}`;
                      const blob = new Blob([demo], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'firmware_config_demo.json'; a.click();
                      URL.revokeObjectURL(url);
                    }}>下载demo</Button>
                }
              </div>
            </Form.Item>
          )}
              </>
            ) : null}
          </Form.Item>
          <Form.Item name="sdk" label="SDK版本(十六进制)" rules={[{ required: true, message: '请输入SDK版本' }]}>
            <Input placeholder="请输入SDK版本号（十六进制）" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => JSON.stringify(prev.fwType) !== JSON.stringify(cur.fwType)}>
            {({ getFieldValue }) => (getFieldValue('fwType') || []).includes('生产固件') ? (
              <>
                <Form.Item name="testItems" label="生产测试项" rules={[{ required: true, message: '请至少选择一项生产测试项' }]}>
                  <Checkbox.Group>
                    <Row gutter={[0, 8]}>
                      {TEST_ITEMS.map(item => <Col span={8} key={item}><Checkbox value={item}>{item}</Checkbox></Col>)}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </>
            ) : null}
          </Form.Item>
          <Form.Item name="desc" label="固件说明"><Input.TextArea rows={3} placeholder="请输入固件说明（选填）" /></Form.Item>
        </Form>
      </Drawer>

      {/* 固件配置弹窗 */}
      <Modal title="固件配置" open={fwConfigVisible} onCancel={() => setFwConfigVisible(false)} destroyOnClose width={1100}
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button onClick={() => setFwConfigVisible(false)}>关闭</Button><Button type="primary" style={{ background: '#fa8c16', borderColor: '#fa8c16' }} onClick={() => { message.success('已保存并导出'); setFwConfigVisible(false); }}>保存并导出</Button><Button type="primary" onClick={() => { message.success('已保存'); setFwConfigVisible(false); }}>保存</Button></div>}>
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px 24px' }}>
            <Form.Item label="硬件类型"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '类型A' }, { value: '2', label: '类型B' }]} /></Form.Item>
            <Form.Item label="硬件板子新旧"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '新板' }, { value: '2', label: '旧板' }]} /></Form.Item>
            <Form.Item label="光源类型"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '红外' }, { value: '2', label: '白光' }, { value: '3', label: '混合' }]} /></Form.Item>
            <Form.Item label="分辨率"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '1080P' }, { value: '2', label: '2K' }, { value: '3', label: '4K' }]} /></Form.Item>
            <Form.Item label="网口配置"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '有线' }, { value: '2', label: '无线' }]} /></Form.Item>
            <Form.Item label="延时关灯开关"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="云台上下方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="云台左右方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="移动追踪上下方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="移动追踪左右方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="IRCUT方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="球机水平镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="球机垂直镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="枪机水平镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="枪机垂直镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="灯板输入逻辑"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '高电平' }, { value: '2', label: '低电平' }]} /></Form.Item>
            <Form.Item label="白光灯控制逻辑"><Select placeholder="请选择" allowClear options={[{ value: '1', label: 'PWM' }, { value: '2', label: 'GPIO' }]} /></Form.Item>
            <Form.Item label="红外灯控制逻辑"><Select placeholder="请选择" allowClear options={[{ value: '1', label: 'PWM' }, { value: '2', label: 'GPIO' }]} /></Form.Item>
            <Form.Item label="光敏类型"><Select placeholder="请选择" allowClear options={[{ value: '1', label: 'ADC' }, { value: '2', label: 'GPIO' }]} /></Form.Item>
            <Form.Item label="喇叭反向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="语言"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '中文' }, { value: '2', label: '英文' }, { value: '3', label: '多语言' }]} /></Form.Item>
            <Form.Item label="云台左右角度 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="云台上下角度 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="云台左右速度 (1-17)"><InputNumber min={1} max={17} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="云台上下速度 (1-17)"><InputNumber min={1} max={17} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="移动追踪上下步长 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="移动追踪左右步长 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="移动追踪复位间隔 (1-3600)"><InputNumber min={1} max={3600} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="播放声音大小 (1-100)"><InputNumber min={1} max={100} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="监听声音大小 (1-100)"><InputNumber min={1} max={100} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="延时关灯时间 (1-12)"><InputNumber min={1} max={12} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="齿轮比 (10-100)"><InputNumber min={10} max={100} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
          </div>
        </Form>
      </Modal>

      {/* 列表查看固件配置 */}
      <Modal title="查看固件配置" open={viewConfigVisible} onCancel={() => setViewConfigVisible(false)} destroyOnClose width={1100}
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button onClick={() => setViewConfigVisible(false)}>关闭</Button><Button type="primary" style={{ background: '#fa8c16', borderColor: '#fa8c16' }} onClick={() => { message.success('已保存并导出'); setViewConfigVisible(false); }}>保存并导出</Button><Button type="primary" onClick={() => { message.success('已保存'); setViewConfigVisible(false); }}>保存</Button></div>}>
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px 24px' }}>
            <Form.Item label="硬件类型"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '类型A' }, { value: '2', label: '类型B' }]} /></Form.Item>
            <Form.Item label="硬件板子新旧"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '新板' }, { value: '2', label: '旧板' }]} /></Form.Item>
            <Form.Item label="光源类型"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '红外' }, { value: '2', label: '白光' }, { value: '3', label: '混合' }]} /></Form.Item>
            <Form.Item label="分辨率"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '1080P' }, { value: '2', label: '2K' }, { value: '3', label: '4K' }]} /></Form.Item>
            <Form.Item label="网口配置"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '有线' }, { value: '2', label: '无线' }]} /></Form.Item>
            <Form.Item label="延时关灯开关"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="云台上下方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="云台左右方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="移动追踪上下方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="移动追踪左右方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="IRCUT方向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="球机水平镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="球机垂直镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="枪机水平镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="枪机垂直镜像翻转"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '开启' }, { value: '0', label: '关闭' }]} /></Form.Item>
            <Form.Item label="灯板输入逻辑"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '高电平' }, { value: '2', label: '低电平' }]} /></Form.Item>
            <Form.Item label="白光灯控制逻辑"><Select placeholder="请选择" allowClear options={[{ value: '1', label: 'PWM' }, { value: '2', label: 'GPIO' }]} /></Form.Item>
            <Form.Item label="红外灯控制逻辑"><Select placeholder="请选择" allowClear options={[{ value: '1', label: 'PWM' }, { value: '2', label: 'GPIO' }]} /></Form.Item>
            <Form.Item label="光敏类型"><Select placeholder="请选择" allowClear options={[{ value: '1', label: 'ADC' }, { value: '2', label: 'GPIO' }]} /></Form.Item>
            <Form.Item label="喇叭反向"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '正向' }, { value: '2', label: '反向' }]} /></Form.Item>
            <Form.Item label="语言"><Select placeholder="请选择" allowClear options={[{ value: '1', label: '中文' }, { value: '2', label: '英文' }, { value: '3', label: '多语言' }]} /></Form.Item>
            <Form.Item label="云台左右角度 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="云台上下角度 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="云台左右速度 (1-17)"><InputNumber min={1} max={17} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="云台上下速度 (1-17)"><InputNumber min={1} max={17} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="移动追踪上下步长 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="移动追踪左右步长 (1-10000)"><InputNumber min={1} max={10000} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="移动追踪复位间隔 (1-3600)"><InputNumber min={1} max={3600} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="播放声音大小 (1-100)"><InputNumber min={1} max={100} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="监听声音大小 (1-100)"><InputNumber min={1} max={100} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="延时关灯时间 (1-12)"><InputNumber min={1} max={12} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="齿轮比 (10-100)"><InputNumber min={10} max={100} placeholder="请输入" style={{ width: '100%' }} /></Form.Item>
          </div>
        </Form>
      </Modal>

      {/* 固件测试 - 选择License */}
      <Modal title="固件测试" open={licenseModalVisible} onCancel={() => setLicenseModalVisible(false)} destroyOnClose
        onOk={handleLicenseSubmit} okText="下发测试" cancelText="取消" width={700}>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: 6 }}
          message="测试License"
          description={
            <span>
              仅用于固件测试，还未生成License？
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginLeft: 4 }}
                onClick={() => {
                  setLicenseModalVisible(false);
                  setViewLicensesVisible(true);
                }}
              >
                点我去生成
              </Button>
              <br />
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                为了避免后续返工，需要你在App绑定此License设备，并测试固件能力是否符合预期（表现正常）
                <br />
                对此固件需要在App环境绑定设备（当前测试License），验证固件功能是否正常，若省去此验证环节，可能会对后续量产的设备在使用上存在一定的影响！
              </span>
            </span>
          }
        />

        <Form form={licenseForm} layout="vertical">
          <Form.Item name="selectedLicenses" label={<Text strong>选择测试 License</Text>} rules={[{ required: true, message: '请至少选择一个License' }]}>
            <Checkbox.Group style={{ width: '100%' }}>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, maxHeight: 300, overflowY: 'auto', padding: '4px 0' }}>
                {generatedLicenses.length > 0 ? (
                  <Table
                    dataSource={generatedLicenses.filter(item => !fwTestRecords.some(t => t.license === item.license))}
                    rowKey="key"
                    size="small"
                    pagination={false}
                    showHeader={false}
                    locale={{ emptyText: '所有已生成的 License 均已下发过测试，如需再次下发请在测试记录中操作' }}
                      columns={[
                       {
                         title: 'License',
                         dataIndex: 'license',
                         render: (text) => {
                           return (
                             <Space>
                               <Checkbox value={text}>{text}</Checkbox>
                               <Tag color="cyan" style={{ fontSize: 11 }}>测试专用</Tag>
                             </Space>
                           );
                         }
                       },
                      {
                        title: 'Action',
                        dataIndex: 'license',
                        align: 'right',
                        render: (text) => <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(text); message.success('已复制'); }}>复制</Button>
                      }
                    ]}
                  />
                ) : (
                    <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无已生成的 License，请点击上方按钮生成" />
                    </div>
                )}
              </div>
            </Checkbox.Group>
          </Form.Item>
        </Form>

        {pendingRecord && <div style={{ marginTop: 12, textAlign: 'right' }}><Text type="secondary" style={{ fontSize: 12 }}>测试固件：{pendingRecord.fwName} {pendingRecord.fwVersion}</Text></div>}
      </Modal>

      {/* OTA能力测试 - 选择License + 迭代版本号 + 上传固件包 */}
      <Modal title="OTA功能测试" open={otaModalVisible} onCancel={() => setOtaModalVisible(false)} destroyOnClose
        onOk={handleOtaSubmit} okText="下发测试" cancelText="取消" width={700}>

        <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 6 }} message={
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>测试固件OTA模块/功能是否丢失</div>
            <div style={{ fontSize: 12 }}>此项测试用于模拟真实升级场景，确保在该固件版本升级逻辑下，设备的 OTA 功能不会丢失。</div>
          </div>
        } />

        <Form form={otaForm} layout="vertical">
          <Form.Item name="selectedLicenses" label={<Text strong>选择测试 License</Text>} rules={[{ required: true, message: '请至少选择一个License' }]}>
            <Checkbox.Group style={{ width: '100%' }}>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, maxHeight: 300, overflowY: 'auto', padding: '4px 0' }}>
                {generatedLicenses.length > 0 ? (
                  <Table
                    dataSource={generatedLicenses.filter(item => !otaTestRecords.some(t => t.license === item.license))}
                    rowKey="key"
                    size="small"
                    pagination={false}
                    showHeader={false}
                    locale={{ emptyText: '所有已生成的 License 均已下发过 OTA 测试，如需再次下发请在测试记录中操作' }}
                    columns={[
                      {
                        title: 'License',
                        dataIndex: 'license',
                        render: (text) => {
                          return (
                            <Space>
                              <Checkbox value={text}>{text}</Checkbox>
                              <Tag color="cyan" style={{ fontSize: 11 }}>测试专用</Tag>
                            </Space>
                          );
                        }
                      },
                      {
                        title: 'Action',
                        dataIndex: 'license',
                        align: 'right',
                        render: (text) => <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(text); message.success('已复制'); }}>复制</Button>
                      }
                    ]}
                  />
                ) : (
                    <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无已生成的 License" />
                    </div>
                )}
              </div>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item name="targetVersion" label={<Text strong>迭代版本号</Text>} extra={`需高于当前固件版本 —— ${pendingRecord?.fwVersion || ''}`} rules={[{ required: true, message: '请输入迭代版本号' }]}>
            <Input placeholder="请输入迭代版本号，例如：v2.0.1" />
          </Form.Item>
          <Form.Item name="otaFile" label={<Text strong>上传迭代固件包</Text>} rules={[{ required: true, message: '请上传固件包' }]}>
            <Upload maxCount={1} beforeUpload={() => false}><Button icon={<UploadOutlined />}>点击上传固件</Button></Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 固件测试记录 */}
      <Drawer title={`固件测试记录 - ${testRecordTitle}`} width={750} open={testRecordVisible} onClose={() => setTestRecordVisible(false)} destroyOnClose>
        <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
          <Input placeholder="输入 License 查询" prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} allowClear value={licenseSearch} onChange={(e) => setLicenseSearch(e.target.value)} style={{ width: 280 }} />
          <Button type="primary" icon={<SearchOutlined />}>查询</Button>
          <Button icon={<ReloadOutlined />} onClick={() => setLicenseSearch('')}>重置</Button>
        </div>
        <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 6 }} message="固件升级状态回传存在一定的延迟（10～20分钟）" />
        <Tabs items={[
          { key: 'fw', label: '固件测试', children: (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ padding: '6px 12px', background: '#f5f5f5', borderRadius: 4, borderLeft: '4px solid #1890ff' }}>
                  <Text strong>{testRecordTitle}</Text>
                </div>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setAddTestType('normal'); testDeviceForm.resetFields(); setAddTestVisible(true); }}>添加测试设备</Button>
              </div>
              <Table columns={testRecordColumns} dataSource={filteredFw} rowKey="key" size="small"
                pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} />
            </>
          )},
          { key: 'ota', label: 'OTA能力测试', children: (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ padding: '6px 12px', background: '#f5f5f5', borderRadius: 4, borderLeft: '4px solid #52c41a' }}>
                  <Text strong>{testRecordTitle}</Text>
                </div>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setAddTestType('ota'); testDeviceForm.resetFields(); setAddTestVisible(true); }}>添加测试设备</Button>
              </div>
              <Table columns={otaRecordColumns} dataSource={filteredOta} rowKey="key" size="small"
                pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} />
            </>
          )},
        ]} />
      </Drawer>

      {/* 修改生产测试项 */}
      <Modal title="修改生产测试项" open={editTestItemsVisible} onCancel={() => setEditTestItemsVisible(false)} destroyOnClose
        onOk={() => { editTestItemsForm.validateFields().then((values) => { setDataSource(prev => prev.map(d => d.key === editTestItemsRecord.key ? { ...d, testItems: values.testItems } : d)); setEditTestItemsVisible(false); message.success('生产测试项已更新'); }).catch(() => {}); }} okText="保存" cancelText="取消" width={550}>
        <Form form={editTestItemsForm} layout="vertical">
          <Form.Item name="testItems" label="生产测试项" rules={[{ required: true, message: '请至少选择一项生产测试项' }]}>
            <Checkbox.Group>
              <Row gutter={[0, 8]}>
                {TEST_ITEMS.map(item => <Col span={8} key={item}><Checkbox value={item}>{item}</Checkbox></Col>)}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加测试设备 */}
      <Modal title={addTestType === 'ota' ? '添加OTA测试设备' : '添加测试设备'} open={addTestVisible} onCancel={() => setAddTestVisible(false)} destroyOnClose width={550}
        onOk={() => { testDeviceForm.validateFields().then(() => { message.success('测试设备已添加'); setAddTestVisible(false); }).catch(() => {}); }} okText="开始测试" cancelText="取消">
        {addTestType === 'ota' && (
          <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 6 }} message={
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>测试固件OTA模块/功能是否丢失</div>
              <div>1. 请选择用于授权验证的 <Text strong>License</Text>。</div>
              <div>2. 必须上传一个<Text strong>高于当前版本</Text>的固件包。</div>
              <div style={{ color: '#fa8c16', marginTop: 4 }}>此项测试用于模拟真实升级场景，确保在该固件版本升级逻辑下，设备的 OTA 功能及稳定性不会因版本迭代而丢失。</div>
            </div>
          } />
        )}
        <Form form={testDeviceForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="selectedLicenses" label={<Text strong>选择测试 License</Text>} rules={[{ required: true, message: '请至少选择一个License' }]}>
            <Checkbox.Group style={{ width: '100%' }}>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, maxHeight: 300, overflowY: 'auto', padding: '4px 0' }}>
                {generatedLicenses.length > 0 ? (
                  <div style={{ padding: '0 8px' }}>
                    {generatedLicenses.filter(record => {
                      const text = record.license;
                      const alreadyUsed = addTestType === 'normal'
                        ? fwTestRecords.some(item => item.license === text)
                        : otaTestRecords.some(item => item.license === text);
                      return !alreadyUsed;
                    }).map(record => {
                      const text = record.license;
                      return (
                        <div key={record.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <Space>
                            <Checkbox value={text}>{text}</Checkbox>
                            <Tag color="cyan" style={{ fontSize: 11 }}>测试专用</Tag>
                          </Space>
                        </div>
                      );
                    })}
                    {generatedLicenses.filter(record => {
                      const text = record.license;
                      return addTestType === 'normal'
                        ? fwTestRecords.some(item => item.license === text)
                        : otaTestRecords.some(item => item.license === text);
                    }).length === generatedLicenses.length && (
                      <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="所有已生成的 License 均已下发过，如需再次下发请在测试记录中操作" />
                      </div>
                    )}
                  </div>
                ) : (
                    <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无已生成的 License" />
                    </div>
                )}
              </div>
            </Checkbox.Group>
          </Form.Item>
          {addTestType === 'ota' && (
            <>
              <Form.Item name="targetVersion" label="迭代版本号（需高于当前版本）" rules={[{ required: true, message: '请输入迭代版本号' }]}>
                <Input placeholder="请输入迭代版本号，例如：v2.0.1" />
              </Form.Item>
              <Form.Item name="otaFile" label="上传迭代固件包（需高于当前版本）" rules={[{ required: true, message: '请上传固件包' }]}>
                <Upload maxCount={1} beforeUpload={() => false}><Button icon={<UploadOutlined />}>点击上传固件</Button></Upload>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* 编辑固件类型 */}
      <Modal title="编辑固件类型" open={editTypeVisible} onCancel={() => { setEditTypeVisible(false); setEditFwConfigEnabled(false); setEditFwConfigUploaded(false); }} destroyOnClose
        onOk={() => {
          editTypeForm.validateFields().then((values) => {
            const newTypes = values.fwType || [];
            const oldTypes = Array.isArray(editTypeRecord.fwType) ? editTypeRecord.fwType : [editTypeRecord.fwType];
            const removed = oldTypes.filter(t => !newTypes.includes(t));
            const hasConfigOrTestItems = editTypeRecord.hasConfig || (editTypeRecord.testItems && editTypeRecord.testItems.length > 0);
            const hasOtaPassed = editTypeRecord.ota === 'pass';

            const doSave = () => {
              const hasProd = newTypes.includes('生产固件');
              setDataSource(prev => prev.map(d => d.key === editTypeRecord.key ? {
                ...d,
                fwType: newTypes,
                // 含生产类型时保存测试项和固件配置
                ...(hasProd ? { testItems: values.testItems || [], hasConfig: editFwConfigEnabled } : {}),
                // 取消生产类型时清除配置和测试项
                ...(removed.includes('生产固件') ? { hasConfig: false, testItems: [] } : {}),
                // 取消升级类型时 OTA 测试记录保留但不再作为判定依据
                ...(removed.includes('升级固件') && d.ota === 'pass' ? {} : {}),
              } : d));
              setEditTypeVisible(false);
              message.success('固件类型已更新');
            };

            if (removed.includes('生产固件') && hasConfigOrTestItems) {
              Modal.confirm({
                title: '确认操作',
                content: '取消生产固件类型将同时清除该固件的固件配置和生产测试项配置，确认继续？',
                okText: '确认', cancelText: '取消',
                onOk: doSave,
              });
            } else if (removed.includes('升级固件') && hasOtaPassed) {
              Modal.confirm({
                title: '确认操作',
                content: '该固件已通过OTA能力测试，取消升级固件类型后OTA测试记录将保留但不再作为升级固件的判定依据，确认继续？',
                okText: '确认', cancelText: '取消',
                onOk: doSave,
              });
            } else {
              doSave();
            }
          }).catch(() => {});
        }}
        okText="保存" cancelText="取消" width={500}
      >
        {editTypeRecord && (
          <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6 }}>
            <Text strong>{editTypeRecord.fwName} {editTypeRecord.fwVersion}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              当前测试状态：
              {editTypeRecord.test === 'pass' ? '功能测试通过' : editTypeRecord.test === 'testing' ? '功能测试中' : '待测试'}
              {editTypeRecord.ota === 'pass' ? ' / OTA测试通过' : editTypeRecord.ota === 'testing' ? ' / OTA测试中' : ''}
            </Text>
          </div>
        )}
        <Form form={editTypeForm} layout="vertical"
          onValuesChange={(changedValues) => {
            if (changedValues.fwType) {
              const hasProdNow = changedValues.fwType.includes('生产固件');
              // 取消生产固件时清除测试项
              if (!hasProdNow) {
                editTypeForm.setFieldsValue({ testItems: [] });
              }
            }
          }}
        >
          <Form.Item name="fwType" label="固件类型" rules={[{ required: true, message: '请至少选择一种固件类型', type: 'array', min: 1 }]}>
            <Checkbox.Group options={[{ label: '生产固件', value: '生产固件' }, { label: '升级固件', value: '升级固件' }]} />
          </Form.Item>
           <Form.Item noStyle shouldUpdate={(prev, cur) => {
            const pHadProd = (prev.fwType || []).includes('生产固件');
            const cHasProd = (cur.fwType || []).includes('生产固件');
            return pHadProd !== cHasProd;
          }}>
            {({ getFieldValue }) => {
              const currentFwType = getFieldValue('fwType') || [];
              const hasProdNow = currentFwType.includes('生产固件');
              if (hasProdNow) {
                return (
                  <Form.Item name="testItems" label="生产测试项" rules={[{ required: true, message: '请至少选择一项生产测试项', type: 'array', min: 1 }]}>
                    <Checkbox.Group>
                      <Row gutter={[0, 8]}>
                        {TEST_ITEMS.map(item => <Col span={8} key={item}><Checkbox value={item}>{item}</Checkbox></Col>)}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => {
            const pHadProd = (prev.fwType || []).includes('生产固件');
            const cHasProd = (cur.fwType || []).includes('生产固件');
            return pHadProd !== cHasProd;
          }}>
            {({ getFieldValue }) => {
              const currentFwType = getFieldValue('fwType') || [];
              const hasProd = currentFwType.includes('生产固件');
              if (!hasProd) return null;
              return (
                <>
                  <Form.Item label={<span>固件配置 <Tooltip title="当前固件是否需要TF卡固件配置？"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>}>
                    <Radio.Group value={editFwConfigEnabled} onChange={(e) => { setEditFwConfigEnabled(e.target.value); if (!e.target.value) { setEditFwConfigUploaded(false); } }}>
                      <Radio value={false}>否</Radio>
                      <Radio value={true}>是</Radio>
                    </Radio.Group>
                  </Form.Item>
                  {editFwConfigEnabled && (
                    <Form.Item label="上传固件配置">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Upload maxCount={1} beforeUpload={() => { setEditFwConfigUploaded(true); return false; }} showUploadList={false} accept=".json">
                          <Button icon={<UploadOutlined />}>上传固件配置</Button>
                        </Upload>
                        {editFwConfigUploaded
                          ? <Button type="link" onClick={() => setFwConfigVisible(true)}>修改配置</Button>
                          : <Button type="link" icon={<DownloadOutlined />} onClick={() => {
                              const demo = `{
  "itemList": [
    {
      "key": "HWType",
      "keyName": "硬件类型",
      "valueType": "0",
      "valueArray": [
        { "value": "0", "valueName": "未定义" },
        { "value": "1", "valueName": "摇头机" }
      ]
    },
    {
      "key": "TdAdjustStepY",
      "keyName": "校准步长Y",
      "valueType": "1",
      "minValue": "1",
      "maxValue": "10000"
    },
    {
      "key": "osd",
      "keyName": "OSD",
      "valueType": "2",
      "maxLen": "1024"
    }
  ]
}`;
                              const blob = new Blob([demo], { type: 'text/plain' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = 'firmware_config_demo.json'; a.click();
                              URL.revokeObjectURL(url);
                            }}>下载demo</Button>
                        }
                      </div>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>
          <Alert type="warning" showIcon style={{ borderRadius: 6, fontSize: 12 }}
            message="修改固件类型后，系统将按新类型重新判定测试要求。取消生产类型将清除关联的固件配置和生产测试项。" />
        </Form>
      </Modal>

      {/* 查看已生成的License */}
      <Modal
        title="测试License管理"
        open={viewLicensesVisible}
        onCancel={() => setViewLicensesVisible(false)}
        destroyOnClose
        width={800}
        footer={<Button onClick={() => setViewLicensesVisible(false)}>关闭</Button>}
      >
        <div style={{ marginBottom: 16 }}>
          <Space size="large">
            <Text type="secondary">产品名称：<Text strong style={{ color: 'rgba(0, 0, 0, 0.88)' }}>{productName}</Text></Text>
            <Text type="secondary">产品ID：<Text strong style={{ color: 'rgba(0, 0, 0, 0.88)' }}>{productId}</Text></Text>
          </Space>
        </div>

        <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 6 }}
          message={
            <span>
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>为了避免后续返工，需要你在App绑定此License设备，并测试固件能力是否符合预期（表现正常）</span>
              <br />
              若测试授权不足以测试使用，可联系平台添加。
            </span>
          }
        />
        <div style={{ marginBottom: 16 }}>
          <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Statistic 
                title={<Space><ExperimentOutlined style={{ color: '#faad14' }} /> <Text type="secondary">测试授权余额</Text></Space>} 
                value={Object.values(quotaByFw).reduce((sum, q) => sum + q.used, 0)} 
                suffix={`/ ${Object.values(quotaByFw).reduce((sum, q) => sum + q.total, 0)}个`} 
                valueStyle={{ color: '#faad14' }}
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                disabled={Object.values(quotaByFw).reduce((sum, q) => sum + q.used, 0) >= Object.values(quotaByFw).reduce((sum, q) => sum + q.total, 0)}
                onClick={() => {
                  const used = Object.values(quotaByFw).reduce((sum, q) => sum + q.used, 0);
                  const total = Object.values(quotaByFw).reduce((sum, q) => sum + q.total, 0);
                  if (used >= total) {
                    message.warning('测试授权余额不足');
                    return;
                  }

                  const dateStr = new Date().toISOString().slice(2, 10).replace(/-/g, ''); // YYMMDD
                  const tIndex = (used + 1).toString().padStart(2, '0');
                  const randomStr = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
                  const newLicense = `A01T${tIndex}${dateStr}${randomStr}`;
                  
                  // 倒序展示：将新生成的放在最前面
                  setGeneratedLicenses([{ license: newLicense, type: 'test', key: Date.now(), generateTime: new Date().toLocaleString() }, ...generatedLicenses]);
                  
                  // 增加已使用额度
                  const firstKey = Object.keys(quotaByFw)[0];
                  setQuotaByFw(prev => ({
                    ...prev,
                    [firstKey]: { ...prev[firstKey], used: prev[firstKey].used + 1 }
                  }));
                  
                  message.success(`已生成测试License: ${newLicense}`);
                }}
              >
                生成1个License
              </Button>
            </div>
          </Card>
        </div>

        <Table 
          dataSource={generatedLicenses} 
          rowKey="key" 
          size="small"
          columns={[
            { title: 'License', dataIndex: 'license', width: 200 },
            { title: '类型', dataIndex: 'type', width: 100, render: (t) => <Tag color="cyan">测试专用</Tag> },
            { title: '生成时间', dataIndex: 'generateTime', width: 170 },
            { title: '操作', width: 80, align: 'right', render: (t, r) => <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(r.license); message.success('已复制'); }}>复制</Button> },
          ]}
        />
      </Modal>
    </div>
  );
};

export default FirmwareDetail;
